import { useSyncExternalStore } from 'react';
import { api } from './api.ts';
import { readSSE, StreamStalledError } from './sse.ts';
import { confirmStagingSpend, stagingAckHeaders } from './stagingGuard.ts';
import type { ChatScope } from './scope.ts';
import type { DeckActivityPayload } from '../../shared/api.ts';

/**
 * Scope-keyed turn store — the engine behind concurrent per-slide AI edits.
 *
 * The chat used to keep one in-flight turn in component state inside
 * useChatStream, so a second instruction (to any slide) was blocked until the
 * first finished, and switching slides discarded the live stream. This store
 * lifts every turn into a module-level Map keyed by scope.key, so each
 * slide/deck scope owns an independent turn that keeps running — and keeps
 * filling its live buffer — no matter which scope the UI is showing. The
 * server was already stateless and concurrent (per-scope threads, per-slide
 * preview state); this is the client catching up.
 *
 * useChatStream(scope) is now a thin selector over getSlice(scope.key). The
 * old `activity` map + `isCurrent()` dance fold into this: each turn writes
 * only to its OWN slice, so there is no "wrong thread" to guard against, and
 * the filmstrip dots read activityState straight off the slices.
 */

// Most concurrent turns allowed at once. Protects the GO gateway + the daily
// token budget from a runaway fan-out; a send past the cap surfaces a friendly
// error on the attempting scope rather than queuing. (3 → 5, Jake 2026-07-29 —
// the Activity tab made parallel edits the normal workflow.)
export const MAX_INFLIGHT = 5;

// ----- the vocabulary -------------------------------------------------------
// None of this was written down before: the store, the SSE frames and the
// persisted thread were all plain objects, so a renamed field surfaced as
// `undefined` in a chat card rather than as an error.

/** One proposal card, as it arrives on the stream and as it is persisted. */
/**
 * A tool call's arguments, as the model wrote them.
 *
 * Every field is optional because WHICH are present depends on `name`, and the
 * two travel as separate fields on the wire — so this cannot be a discriminated
 * union without restructuring what the server sends. The surfaces that read it
 * (ToolCard's summary, the proposal actions) switch on `name` first, which is
 * what makes reading a field here safe. `unknown` was the honest type before a
 * reader existed; now that one does, naming the fields is more honest still.
 */
export type ToolInput = {
  jsx?: string;
  new_jsx?: string;
  rationale?: string;
  slide_id?: string;
  section?: string;
  filename?: string;
  key?: string;
  name?: string;
  color?: string;
  section_key?: string;
  from_primitive?: string;
  to_primitive?: string;
  order?: string[];
  /** Stock/bank media the proposal wants fetched. `unsplash_sources` is the
   *  older spelling, still sent by older threads. */
  media_sources?: unknown[];
  unsplash_sources?: unknown[];
  [field: string]: unknown;
};

export type ToolCall = {
  id: string;
  name: string;
  input?: ToolInput;
  applied?: boolean;
  /** Terminal status once the user decides — see `isCardResolved` in tools.ts,
   *  which is the ONE place that answers "has this been dealt with". */
  status?: string;
};

export type TextBlock = { type: 'text'; text: string };
export type ToolUseBlock = { type: 'tool_use' } & ToolCall;
export type ChatBlock = TextBlock | ToolUseBlock;

/** A persisted thread message. `content` is a bare string for a user message
 *  and a block list for an assistant turn. */
export type ChatMessage = {
  role: 'user' | 'assistant';
  content: string | ChatBlock[];
  ts?: number;
};

export type ThreadPayload = { messages?: ChatMessage[] };

/** Token counts for the last reply, totalled over its rounds. `contextTokens`
 *  is the BIGGEST SINGLE PROMPT of the turn, which is what the thread-length
 *  nudge reads — see threadLimits.ts for why that, not the summed input. */
export type TurnUsage = {
  inputTokens?: number;
  outputTokens?: number;
  contextTokens?: number;
  cacheReadInputTokens?: number;
};

export type TurnStatus = { kind: 'idle' } | { kind: 'streaming' } | { kind: 'error'; message: string };

/** The live buffer while a turn runs. `reasoning` is liveness only, never
 *  persisted. */
export type StreamingBuffer = { text: string; tools: ToolCall[]; reasoning?: string };

/** Selection turns only: the server reports each slide as its file is written,
 *  the sole mid-run signal (cards all land at the end). */
export type TurnProgress = { done: number; total: number; doneIds: string[] };

/** 'streaming' | 'unread' drives the filmstrip dots; null is quiet. */
export type ActivityState = 'streaming' | 'unread' | null;

export type TurnSlice = {
  messages: ChatMessage[];
  streaming: StreamingBuffer | null;
  status: TurnStatus;
  usage: TurnUsage | null;
  /** Has the thread been fetched at least once. */
  loaded: boolean;
  slideId: string | null;
  /** Every slide id the running turn targets (selection turns list N). */
  slideIds: string[] | null;
  progress: TurnProgress | null;
  /** For the active-edits tray's label and jump. */
  kind: ChatScope['kind'] | null;
  label: string | null;
  activityState: ActivityState;
  /** Kept so reloadDeckThreads can re-fetch a slice without a scope object. */
  slug?: string;
  threadPath?: string;
};

/** What the chat stream sends. The server's `send()` takes `unknown`, so this
 *  is the client stating the contract it reads — asserted once where the frame
 *  arrives rather than at each of the six field reads below. */
export type ChatEvent =
  | { type: 'text'; delta: string }
  | { type: 'reasoning'; delta: string }
  | { type: 'progress'; done: number; total: number; slideId: string }
  | ({ type: 'tool_use' } & ToolCall)
  | { type: 'done'; usage?: TurnUsage }
  | { type: 'error'; message: string };

/** Options for one send. The wire carries EITHER `fast` OR `claudeModel` —
 *  `sendOptsFor` in models.ts is the one place that knows that. */
export type TurnOptions = {
  fast?: boolean;
  claudeModel?: string | null;
  /** Names a non-Claude engine outright ('pi'). Claude turns say nothing
   *  here and ride on `claudeModel`, as they always have. */
  engine?: string;
  /** The pill's per-message reasoning rung. Applies to every engine, so it
   *  rides alongside whichever one was chosen rather than on any one branch. */
  effort?: string;
};

/**
 * The three endings the catch block below has to tell apart, as real classes
 * rather than fields stapled onto an Error. Which one it is decides whether the
 * turn waits for the server (a dropped pipe: the run is still going) or reports
 * a failure (the server already said it failed), so the distinction is
 * load-bearing, not cosmetic.
 */

/** The server answered non-2xx before the stream began. */
export class ChatHttpError extends Error {
  readonly status: number;
  readonly data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

/** A turn-level failure the server reported ON the stream — final, no recovery. */
export class TurnError extends Error {}

const turns = new Map<string, TurnSlice>(); // scopeKey -> TurnSlice
const aborts = new Map<string, AbortController>(); // scopeKey -> in-flight turn
// scopeKey -> { slug, threadKey } for the in-flight turn, so stopTurn can tell
// the SERVER to cancel (aborting the fetch alone no longer stops a Claude run —
// a lost connection must not bin minutes of work, so the server keeps going
// unless it's told to stop explicitly).
const stopMeta = new Map<string, StopTarget>();
const loading = new Set<string>(); // scopeKeys with an in-flight ensureThread fetch
// Scopes a mounted hook is currently showing. A turn that completes while its
// scope is NOT viewed flags 'unread' (a filmstrip dot); one that's on screen
// clears silently. A Set (not a single key) because the library overlay can
// be open over the canvas — two viewed scopes at once, different keys.
const viewed = new Set<string>();
const listeners = new Set<() => void>();
let version = 0;

/** Enough to stop a run from a tab that never started it — the Activity tab's
 *  Running rows come from the SERVER, so they carry no local stopMeta. */
export type StopTarget = { slug: string; threadKey: string };

const EMPTY: TurnSlice = Object.freeze<TurnSlice>({
  messages: [],
  streaming: null,
  status: { kind: 'idle' },
  usage: null,
  loaded: false,
  slideId: null,
  slideIds: null,
  progress: null,
  kind: null,
  label: null,
  activityState: null,
});

// ----- store plumbing (useSyncExternalStore) -------------------------------

function emit() {
  version += 1;
  for (const fn of listeners) fn();
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getVersion(): number {
  return version;
}

export function getSlice(key: string | null | undefined): TurnSlice {
  return (key && turns.get(key)) || EMPTY;
}

function setSlice(key: string, patch: Partial<TurnSlice>) {
  turns.set(key, { ...(turns.get(key) || EMPTY), ...patch });
  emit();
}

/** Client mirror of the server's markToolStatus (thread-store.ts): stamp a
 *  terminal status onto a tool_use block in a scope's loaded slice. The server
 *  persists apply/reject/handoff correctly, but the slice is fetched once
 *  (ensureThread short-circuits on `loaded`) and never refreshed — so without
 *  this a resolved card re-renders as an actionable `pending` proposal after a
 *  scope round-trip remounts it. Writing the status here feeds ToolCard's
 *  re-hydration effect so the card resolves immediately and stays resolved.
 *  No-op when the slice is absent/unloaded or the block already has that status. */
export function setToolStatus(scopeKey: string, toolId: string | null | undefined, status: string) {
  const cur = turns.get(scopeKey);
  if (!cur || !Array.isArray(cur.messages) || !toolId) return;
  let changed = false;
  const messages: ChatMessage[] = cur.messages.map((m) => {
    if (!Array.isArray(m.content)) return m;
    let touched = false;
    const content = m.content.map((b) => {
      if (b.type === 'tool_use' && b.id === toolId && b.status !== status) {
        touched = true;
        return { ...b, status };
      }
      return b;
    });
    if (!touched) return m;
    changed = true;
    return { ...m, content };
  });
  if (changed) setSlice(scopeKey, { messages });
}

function inFlightCount() {
  let n = 0;
  for (const s of turns.values()) if (s.status.kind === 'streaming') n += 1;
  return n;
}

// ----- derived selectors (version-cached for referential stability) --------

// The store outlives any one deck — it is module-level and the editor is an
// SPA, so opening deck B leaves deck A's slices (and a run still streaming in
// A) in the same Map. Every surface here is a view of ONE deck, so both
// selectors take the slug and filter to it: an agent working in another deck
// is not this editor's business (Jake, 2026-08-01). Scope keys are
// `${slug}:${slideId | _deck}` and a slug never contains a colon, so the key
// itself is the deck, with no dependence on a slice's `slug` field being set.
/** The manifest row a `base#instance` id belongs to. `split` always yields a
 *  first element; `?? id` states that without an assertion. */
const baseIdOf = (id: string): string => id.split('#')[0] ?? id;

function slugOfKey(key: string): string {
  const i = String(key).indexOf(':');
  return i === -1 ? '' : String(key).slice(0, i);
}

/** One entry of the filmstrip's dot map. */
export type ActivityEntry = {
  slideId: string | null;
  slideIds: string[] | null;
  progress: TurnProgress | null;
  state: ActivityState;
  kind: ChatScope['kind'] | null;
  label: string | null;
};

let activityCache: Record<string, ActivityEntry> | null = null;
let activityCacheVer = -1;
let activityCacheSlug: string | null | undefined = null;
/** { [scopeKey]: { slideId, state } } — the shape Canvas feeds the filmstrip,
 *  for the given deck only. Cached against `version` (and the slug) so an
 *  unchanged store returns the same object and Canvas's useMemo([chat.activity])
 *  doesn't churn every render. */
export function activitySnapshot(slug?: string | null): Record<string, ActivityEntry> {
  if (activityCacheVer === version && activityCacheSlug === slug && activityCache) return activityCache;
  const out: Record<string, ActivityEntry> = {};
  for (const [key, s] of turns.entries()) {
    if (slug && slugOfKey(key) !== slug) continue;
    if (s.activityState)
      out[key] = {
        slideId: s.slideId,
        slideIds: s.slideIds,
        progress: s.progress,
        state: s.activityState,
        kind: s.kind,
        label: s.label,
      };
  }
  activityCache = out;
  activityCacheVer = version;
  activityCacheSlug = slug;
  return out;
}

/** A turn the THIS TAB knows about — running now, or finished unread. The
 *  Activity panel merges these with the server's own running list. */
export type ActiveTurn = {
  key: string;
  slug: string;
  slideId: string | null;
  slideIds: string[] | null;
  progress: TurnProgress | null;
  kind: ChatScope['kind'] | null;
  label: string | null;
  state: 'streaming' | 'unread';
};

let activeCache: ActiveTurn[] | null = null;
let activeCacheVer = -1;
let activeCacheSlug: string | null | undefined = null;
function activeTurnsList(slug?: string | null): ActiveTurn[] {
  if (activeCacheVer === version && activeCacheSlug === slug && activeCache) return activeCache;
  const out: ActiveTurn[] = [];
  for (const [key, s] of turns.entries()) {
    if (slug && slugOfKey(key) !== slug) continue;
    const running = s.status.kind === 'streaming';
    if (!running && s.activityState !== 'unread') continue;
    out.push({
      key,
      slug: slugOfKey(key),
      slideId: s.slideId,
      slideIds: s.slideIds,
      progress: running ? s.progress : null,
      kind: s.kind,
      label: s.label,
      state: running ? 'streaming' : 'unread',
    });
  }
  activeCache = out;
  activeCacheVer = version;
  activeCacheSlug = slug;
  return out;
}

/** Subscribe a component to ONE deck's running/unread turn list. Pass the slug
 *  of the deck being edited — omitting it returns every deck's turns, which is
 *  what made another deck's agent show up in this deck's Activity tab. */
export function useActiveTurns(slug?: string | null): ActiveTurn[] {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  return activeTurnsList(slug);
}

/** Subscribe to ONE scope's slice without OPENING it: for a surface that
 *  WATCHES a turn it fired but doesn't display (the change-template modal's
 *  working state).
 *
 *  Deliberately not useChatStream: that hook's effect calls openScope/closeScope,
 *  which marks the scope viewed and clears its unread dot. Watching through it
 *  would silently swallow the "AI finished" toast and the filmstrip dot for a
 *  turn the user never actually read. `key` may be null: getSlice returns the
 *  frozen EMPTY slice, so this stays hook-safe when nothing is running. */
export function useTurnSlice(key: string | null | undefined): TurnSlice {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  return getSlice(key);
}

// ----- lifecycle ------------------------------------------------------------

/** Load the persisted thread once per scope. Unlike the old per-switch effect
 *  this NEVER discards a live streaming buffer — switching away and back to a
 *  running turn re-attaches to it because the buffer lives in the slice. */
export async function ensureThread(scope: ChatScope | null | undefined): Promise<void> {
  if (!scope) return;
  const key = scope.key;
  const cur = turns.get(key);
  if (cur && cur.loaded) return;
  if (loading.has(key)) return;
  loading.add(key);
  try {
    const t = await api.get<ThreadPayload>(scope.threadPath);
    setSlice(key, {
      messages: t.messages || [],
      loaded: true,
      slideId: scope.slide?.id || null,
      kind: scope.kind,
      label: scope.label,
      // Kept so reloadDeckThreads can re-fetch this slice without a scope object.
      slug: scope.slug,
      threadPath: scope.threadPath,
    });
  } catch {
    setSlice(key, {
      messages: [],
      loaded: true,
      slideId: scope.slide?.id || null,
      kind: scope.kind,
      label: scope.label,
      slug: scope.slug,
      threadPath: scope.threadPath,
    });
  } finally {
    loading.delete(key);
  }
}

/** A hook is now showing this scope: load its thread and clear a waiting
 *  ('unread') dot. A still-streaming turn keeps its dot. */
export function openScope(scope: ChatScope | null | undefined) {
  if (!scope) return;
  viewed.add(scope.key);
  ensureThread(scope);
  const s = turns.get(scope.key);
  if (s && s.activityState === 'unread') setSlice(scope.key, { activityState: null });
}

export function closeScope(key: string | null | undefined) {
  if (key) viewed.delete(key);
}

// ----- the turn --------------------------------------------------------------

/** Run one chat turn against `scope`. Ported from useChatStream's old send():
 *  same wire protocol, but every state write targets the captured `sentKey`'s
 *  slice — so a turn keeps streaming into its own slice while the user works
 *  on another. Turns run the server's `claude -p` path by default; opts.fast
 *  routes the turn to the metered chat model instead. */
export async function runTurn(
  scope: ChatScope | null | undefined,
  rawPrompt: string,
  opts: TurnOptions = {},
): Promise<void> {
  const prompt = (rawPrompt || '').trim();
  if (!prompt || !scope) return;
  const sentKey = scope.key;
  // Already a turn on THIS scope — silent no-op (matches the old gate). A
  // different scope is free to run concurrently.
  if (getSlice(sentKey).status.kind === 'streaming') return;
  if (inFlightCount() >= MAX_INFLIGHT) {
    setSlice(sentKey, {
      status: {
        kind: 'error',
        message: `Up to ${MAX_INFLIGHT} edits can run at once: wait for one to finish, or stop one.`,
      },
    });
    return;
  }

  const sentSlideId = scope.slide?.id || null;
  // Every slide this turn will write to (base/file-level ids) — a selection
  // lists N, a slide turn its one, a whole-deck turn none (open-ended).
  const sentSlideIds = scope.selection || (sentSlideId ? [baseIdOf(sentSlideId)] : null);
  // Per-slide lock: refuse a turn whose target slides overlap a turn that is
  // still streaming — two agents editing one slide would race, and the loser's
  // proposal silently builds on stale content.
  if (sentSlideIds) {
    const wanted = new Set(sentSlideIds);
    for (const s of turns.values()) {
      if (s.status.kind !== 'streaming' || !s.slideIds) continue;
      const clash = s.slideIds.find((id) => wanted.has(id));
      if (clash) {
        setSlice(sentKey, {
          status: {
            kind: 'error',
            message: `AI is already working on ${s.label || 'that slide'}: wait for it to finish, or stop it from the Activity tab.`,
          },
        });
        return;
      }
    }
  }
  // AFTER the concurrency guards and BEFORE the optimistic write below, so
  // declining leaves the transcript exactly as it was rather than stranding a
  // user message nothing answered. One gate for every AI edit in the studio:
  // typed chat, and the turns the studio fires on the user's behalf (change
  // template, describe slide, merge, split, review) all arrive here.
  if (!(await confirmStagingSpend('making this change'))) return;
  const ackHeaders = await stagingAckHeaders();
  const { chatPath, threadPath } = scope;

  setSlice(sentKey, {
    messages: [...getSlice(sentKey).messages, { role: 'user', content: prompt, ts: Date.now() }],
    streaming: { text: '', tools: [] },
    status: { kind: 'streaming' },
    activityState: 'streaming',
    slideId: sentSlideId,
    slideIds: sentSlideIds,
    progress: null,
    kind: scope.kind,
    label: scope.label,
    loaded: true,
  });

  const ctrl = new AbortController();
  aborts.set(sentKey, ctrl);
  const sentThreadKey = scope.kind === 'slide' ? baseIdOf(sentSlideId || '') : '_deck';
  stopMeta.set(sentKey, { slug: scope.slug, threadKey: sentThreadKey });
  const sentAt = Date.now();
  let assistant = '';
  let reasoning = '';
  let toolCalls: ToolCall[] = [];
  try {
    const res = await fetch(chatPath, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...ackHeaders },
      body: JSON.stringify({
        prompt,
        ...(scope.selection ? { selection: scope.selection } : {}),
        // Default turns run the server's Claude path; `fast: true` opts this
        // turn onto the metered chat model and `engine: 'pi'` onto the
        // ChatGPT-plan agent. No claudeModel → the server falls back to the ⚙
        // default, rather than this layer quietly pinning every turn to Sonnet.
        ...(opts.fast
          ? { fast: true }
          : opts.engine
            ? { engine: opts.engine }
            : opts.claudeModel
              ? { claudeModel: opts.claudeModel }
              : {}),
        // The pill's per-message reasoning rung. Rides ALONGSIDE the engine
        // rather than instead of it, and is absent on most turns — no rung
        // means the server's ⚙ default, which is the shared setting.
        ...(opts.effort ? { effort: opts.effort } : {}),
      }),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ChatHttpError(data.error || `HTTP ${res.status}`, res.status, data);
    }
    const completed = await readSSE(
      res,
      (frame) => {
        // Asserted once, here, rather than at each field read below: this is
        // our own server's stream and ChatEvent is what it sends.
        const payload = frame as ChatEvent;
        if (payload.type === 'text') {
          assistant += payload.delta;
          setSlice(sentKey, { streaming: { text: assistant, tools: toolCalls, reasoning } });
        } else if (payload.type === 'reasoning') {
          // Thinking deltas from reasoning models — liveness only, never persisted.
          reasoning += payload.delta;
          setSlice(sentKey, { streaming: { text: assistant, tools: toolCalls, reasoning } });
        } else if (payload.type === 'progress') {
          // Selection turns: the server reports each selected slide as its file
          // is written — the only mid-run signal (cards land at the end).
          const cur = getSlice(sentKey).progress || { done: 0, total: payload.total, doneIds: [] };
          setSlice(sentKey, {
            progress: {
              done: payload.done,
              total: payload.total,
              doneIds: cur.doneIds.includes(payload.slideId) ? cur.doneIds : [...cur.doneIds, payload.slideId],
            },
          });
        } else if (payload.type === 'tool_use') {
          toolCalls = [
            ...toolCalls,
            { id: payload.id, name: payload.name, input: payload.input, applied: payload.applied },
          ];
          setSlice(sentKey, { streaming: { text: assistant, tools: toolCalls, reasoning } });
        } else if (payload.type === 'done') {
          setSlice(sentKey, { usage: payload.usage });
          return true; // stop reading — the turn is complete
        } else if (payload.type === 'error') {
          // A turn-level failure the server reported — final, no recovery.
          throw new TurnError(payload.message);
        }
      },
      { stallMs: 90_000 },
    );
    // The stream closed without a done/error event — the pipe died, not the
    // turn. Fall through to the same server-side recovery as a stall.
    if (!completed) throw new StreamStalledError('The connection closed mid-turn.');
    // Turn complete. The server has persisted the full turn — re-sync from it
    // (so navigating away/back mid-stream stays consistent), and flag 'unread'
    // unless the user is watching this scope right now.
    const seen = viewed.has(sentKey);
    try {
      const t = await api.get<ThreadPayload>(threadPath);
      setSlice(sentKey, {
        messages: t.messages || [],
        streaming: null,
        progress: null,
        status: { kind: 'idle' },
        loaded: true,
        activityState: seen ? null : 'unread',
      });
    } catch {
      const finalContent: ChatBlock[] = [];
      if (assistant) finalContent.push({ type: 'text', text: assistant });
      for (const t of toolCalls)
        finalContent.push({ type: 'tool_use', id: t.id, name: t.name, input: t.input, applied: t.applied });
      setSlice(sentKey, {
        messages: [...getSlice(sentKey).messages, { role: 'assistant', content: finalContent, ts: Date.now() }],
        streaming: null,
        progress: null,
        status: { kind: 'idle' },
        activityState: seen ? null : 'unread',
      });
    }
  } catch (err) {
    // A stop aborts our fetch immediately, but the SERVER is still finishing:
    // it contains the run's side effects, keeps whatever slides were already
    // done as reviewable cards, and persists the outcome. Poll the thread for
    // that instead of inventing a local "(Stopped by the user.)" line — the old
    // note lived in this tab's memory only, so the run left no Activity row and
    // vanished on reload (2026-07-30).
    const stoppedNote = async ({ poll = true } = {}) => {
      // Containment runs under the deck lock and the kill escalates after a
      // short grace, so the outcome lands in seconds — poll briefly, not for
      // the 25 minutes a dropped connection warrants.
      if (poll) {
        const recovered = await recoverTurn(sentKey, threadPath, sentAt, ctrl, {
          deadlineMs: 45_000,
          intervalMs: 2000,
          ignoreAbort: true,
        });
        if (recovered === 'landed') return;
      }
      // The server never landed anything (it was killed, or the run had already
      // finished and there was nothing to stop). Say so rather than pretending.
      const finalContent: ChatBlock[] = [{ type: 'text', text: (assistant ? assistant + '\n\n' : '') + '(Stopped.)' }];
      for (const t of toolCalls)
        finalContent.push({ type: 'tool_use', id: t.id, name: t.name, input: t.input, applied: t.applied });
      setSlice(sentKey, {
        messages: [...getSlice(sentKey).messages, { role: 'assistant', content: finalContent, ts: Date.now() }],
        streaming: null,
        progress: null,
        status: { kind: 'idle' },
        activityState: null,
      });
    };
    if (err instanceof Error && err.name === 'AbortError') {
      setSlice(sentKey, {
        streaming: {
          text: assistant,
          tools: toolCalls,
          reasoning: reasoning + '\n[stopping, tidying up the deck…]\n',
        },
      });
      await stoppedNote();
      return; // the finally below fires studio:activity-refresh
    }
    // Transport loss (stall, dropped pipe, network error) — the server keeps
    // running and persists the outcome, so wait for THAT instead of binning
    // the turn. Server-reported turn errors and HTTP failures skip this.
    if (!(err instanceof TurnError) && !(err instanceof ChatHttpError)) {
      setSlice(sentKey, {
        streaming: {
          text: assistant,
          tools: toolCalls,
          reasoning: reasoning + '\n[connection lost, waiting for the server to finish…]\n',
        },
      });
      // Watch the SERVER's in-flight list as well as the thread: a restart (a
      // deploy, or `bun --watch` locally) takes the run with it, and nothing
      // will ever land — waiting out the full 25 minutes left the Activity tab
      // reading "working…" on a slide that was already finished (Jake,
      // 2026-08-01).
      const outcome = await recoverTurn(sentKey, threadPath, sentAt, ctrl, {
        watchRun: { slug: scope.slug, threadKey: sentThreadKey },
      });
      if (outcome === 'landed') return;
      if (ctrl.signal.aborted) {
        // Stopped while we were waiting out a dead pipe. recoverTurn already
        // polled the thread and found nothing, so go straight to the note.
        await stoppedNote({ poll: false });
        return;
      }
      setSlice(sentKey, {
        streaming: null,
        progress: null,
        status: {
          kind: 'error',
          message:
            outcome === 'gone'
              ? 'The server stopped running this edit (it may have restarted). Anything it had already written is on the slide: check it, and the Activity tab, before retrying.'
              : 'Lost the connection and the server never reported a result: check the Activity tab before retrying.',
        },
        activityState: viewed.has(sentKey) ? null : 'unread',
      });
      return;
    }
    setSlice(sentKey, {
      streaming: null,
      progress: null,
      status: { kind: 'error', message: err?.message || 'Chat failed' },
      activityState: viewed.has(sentKey) ? null : 'unread',
    });
  } finally {
    if (aborts.get(sentKey) === ctrl) aborts.delete(sentKey);
    if (stopMeta.get(sentKey)?.threadKey !== undefined) stopMeta.delete(sentKey);
    // The turn's cards are persisted server-side now — tell every mounted
    // useDeckActivity (the Activity tab, the filmstrip dot merge) to refetch,
    // otherwise a finished proposal vanishes from the panel: the Running row
    // drops off the live store but the durable Changes list is still stale.
    try {
      window.dispatchEvent(new Event('studio:activity-refresh'));
    } catch {
      /* non-browser context */
    }
  }
}

/**
 * Cancel the in-flight turn for one scope. Safe from anywhere (the tray stops a
 * turn whose scope isn't mounted) because the registry is module-level.
 *
 * Resolves to what the server actually did: `{ stopped: true }` means a live run
 * was killed and is now containing its side effects; `{ stopped: false }` means
 * there was nothing to stop — it had already finished, or another tab got there
 * first. The caller says which, rather than reporting a cancel that never was.
 *
 * `target` lets a row from the SERVER's running list (a tab that never started
 * the run, so has no local stopMeta) stop it anyway.
 */
export async function stopTurn(
  key: string | null | undefined,
  target: StopTarget | null = null,
): Promise<{ stopped: boolean }> {
  // Tell the SERVER first — a lost connection no longer cancels a Claude run,
  // so aborting the fetch alone would leave the run finishing headless.
  // `key` may be absent (a scope-less hook calling stop()); a `target` can
  // still stop a run the tab never started, so this is not an early return.
  const meta = target || (key ? stopMeta.get(key) : null);
  let stopped = false;
  if (meta?.slug) {
    try {
      const res = await api.post<{ stopped?: boolean }>(`/api/chat/${meta.slug}/stop`, {
        threadKey: meta.threadKey,
      });
      stopped = !!res?.stopped;
    } catch {
      /* the abort below still detaches this client */
    }
  }
  if (key) aborts.get(key)?.abort();
  return { stopped };
}

/**
 * The stream ended but the server keeps working and persists its outcome. Poll
 * the thread for an assistant message newer than the send; when it lands,
 * finalize the slice from server truth exactly like a live completion.
 *
 * Two callers with different clocks: a DROPPED PIPE waits out the longest
 * server run cap (25 min, 8s ticks), while a STOP only waits for containment
 * to finish (well under a minute) and must ignore its own abort signal — the
 * abort is what started this.
 *
 * `opts.watchRun` ({slug, threadKey}) adds the other terminal condition, the
 * one the build flows already have (`lib/buildProgress.js`): a run that is
 * GONE must end the wait. A server restart — a deploy, or `bun --watch`
 * locally — takes the run and its `activeRuns` entry with it, so no message
 * will ever land and the poll would otherwise sit on "working…" for the full
 * 25 minutes with the edit already finished on the slide. The server persists
 * the turn BEFORE dropping it from `activeRuns`, so a run missing from the
 * list twice running, with still nothing on the thread, is really gone.
 *
 * Returns 'landed' | 'gone' | 'timeout'.
 */
type RecoverOptions = {
  deadlineMs?: number;
  intervalMs?: number;
  /** A STOP must ignore its own abort — the abort is what started this. */
  ignoreAbort?: boolean;
  watchRun?: StopTarget | null;
};

async function recoverTurn(
  sentKey: string,
  threadPath: string,
  sentAt: number,
  ctrl: AbortController | null,
  opts: RecoverOptions = {},
): Promise<'landed' | 'gone' | 'timeout'> {
  const { deadlineMs = 25 * 60_000, intervalMs = 8000, ignoreAbort = false, watchRun = null } = opts;
  const aborted = () => !ignoreAbort && !!ctrl?.signal?.aborted;
  const deadline = Date.now() + deadlineMs;
  let missing = 0; // consecutive polls where the server didn't list this run
  while (Date.now() < deadline) {
    if (aborted()) return 'timeout';
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    if (aborted()) return 'timeout';
    try {
      const t = await api.get<ThreadPayload>(threadPath);
      const landed = (t.messages || []).some((m) => m.role === 'assistant' && (m.ts || 0) >= sentAt - 30_000);
      if (landed) {
        const seen = viewed.has(sentKey);
        setSlice(sentKey, {
          messages: t.messages || [],
          streaming: null,
          progress: null,
          status: { kind: 'idle' },
          loaded: true,
          activityState: seen ? null : 'unread',
        });
        return 'landed';
      }
      if (watchRun?.slug && watchRun.threadKey) {
        const h = await api.get<Partial<DeckActivityPayload>>(`/api/chat/${watchRun.slug}/history`);
        const live = (h?.running || []).some((r) => r.threadKey === watchRun.threadKey);
        missing = live ? 0 : missing + 1;
        if (missing >= 2) return 'gone';
      }
    } catch {
      /* transient — keep polling, and don't count it as a missing run */
      missing = 0;
    }
  }
  return 'timeout';
}

/** Re-pull the persisted thread for a scope into its slice. Used after a batch
 *  "Apply all", whose server-side markToolStatus writes 'applied' onto each
 *  tool_use block — reloading re-hydrates those statuses so the cards stop
 *  offering Apply without tracking them card-by-card on the client. */
export async function reloadThread(scope: ChatScope | null | undefined): Promise<void> {
  if (!scope) return;
  try {
    const t = await api.get<ThreadPayload>(scope.threadPath);
    setSlice(scope.key, { messages: t.messages || [], loaded: true });
  } catch {
    /* keep current messages on a failed refetch */
  }
}

/**
 * Re-pull EVERY loaded thread for a deck.
 *
 * A proposal card lives in one thread but is actionable from two surfaces, and
 * the Activity tab lists rows from every thread in the deck at once. Reloading
 * only the scope the user happens to be looking at leaves the others stale —
 * they are `loaded`, so ensureThread won't re-fetch them, and the card still
 * offers Apply when the user navigates back. Called after any action that
 * settles a proposal, from either surface.
 */
export async function reloadDeckThreads(slug: string | null | undefined): Promise<void> {
  if (!slug) return;
  const targets = [];
  for (const [key, slice] of turns) {
    if (slice?.slug === slug && slice.threadPath) targets.push({ key, threadPath: slice.threadPath });
  }
  await Promise.all(
    targets.map(async ({ key, threadPath }) => {
      try {
        const t = await api.get<ThreadPayload>(threadPath);
        setSlice(key, { messages: t.messages || [], loaded: true });
      } catch {
        /* keep current messages on a failed refetch */
      }
    }),
  );
}

export async function clearTurn(scope: ChatScope | null | undefined): Promise<void> {
  if (!scope) return;
  await api.delete(scope.threadPath);
  setSlice(scope.key, {
    messages: [],
    streaming: null,
    usage: null,
    status: { kind: 'idle' },
    activityState: null,
    loaded: true,
  });
}
